/**
 * The user's Rocket.Chat username, on their detail screen — this module's
 * contribution to core's `user.detail.sections` slot.
 *
 * A panel that shows it, for `user.read` (the slot filters on the manifest's
 * `anyOf`), so the screen answers "will this person get a direct message"
 * without opening anything. In its title row an edit button and its dialog,
 * for `user.update` only: a username decides who reads a direct message, so it
 * is set by an administrator and never by the user.
 *
 * Emptying the field removes the username: direct messages to this person
 * stop, and nothing goes to a room instead.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ROCKETCHAT_LIMITS, ROCKETCHAT_USERNAME_PATTERN } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { Details } from '../../components/Details';
import { FormDialog } from '../../components/FormDialog';
import { Panel } from '../../components/Panel';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import type { SlotProps } from '../types';
import { clearHandle, getHandle, setHandle } from './api';

const USERNAME = new RegExp(ROCKETCHAT_USERNAME_PATTERN);

export function RocketChatHandlePanel({ user }: SlotProps['user.detail.sections']) {
  const { t } = useTranslation(['rocketchat', 'common']);
  const { can } = useAuth();
  const handle = useAsync(() => getHandle(user.id), [user.id]);
  const current = handle.data?.handle.username ?? null;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const value = draft.trim();
  const problem =
    value.startsWith('@') || value.startsWith('#')
      ? t('rocketchat:dialog.noPrefix')
      : value !== '' && !USERNAME.test(value)
        ? t('rocketchat:dialog.invalid', { max: ROCKETCHAT_LIMITS.username })
        : null;

  const save = useSubmit(async () => {
    const response = value === '' ? await clearHandle(user.id) : await setHandle(user.id, value);
    handle.setData(response);
    return response;
  });

  function openDialog() {
    setDraft(current ?? '');
    save.clearError();
    setOpen(true);
  }

  // Presentation only: the server still checks `user.update` on every change.
  // No button until the username loads: the dialog starts from it.
  const editable = can('user.update') && handle.data !== undefined;

  return (
    <Panel
      icon={<ForumOutlinedIcon fontSize="small" />}
      title={t('rocketchat:user.title')}
      actions={
        editable && (
          <Button size="small" onClick={openDialog}>
            {current === null ? t('rocketchat:user.set') : t('common:action.edit')}
          </Button>
        )
      }
    >
      <AsyncContent state={handle} placeholder="spinner">
        {({ handle: { username } }) => (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {username === null ? t('rocketchat:user.none') : t('rocketchat:user.description')}
            </Typography>
            <Details rows={[[t('rocketchat:user.username'), username !== null && `@${username}`]]} />
          </Stack>
        )}
      </AsyncContent>

      {editable && (
        <FormDialog
          open={open}
          title={t('rocketchat:dialog.title', { name: user.displayName })}
          submitLabel={value === '' && current !== null ? t('rocketchat:dialog.remove') : undefined}
          pending={save.pending}
          error={save.error}
          disabled={problem !== null || (value === '' && current === null) || value === current}
          maxWidth="xs"
          onSubmit={() => {
            void save.submit().then((saved) => {
              if (saved) setOpen(false);
            });
          }}
          onClose={() => setOpen(false)}
        >
          <TextField
            autoFocus
            label={t('rocketchat:dialog.label')}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            error={problem !== null}
            helperText={problem ?? t('rocketchat:dialog.help')}
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">@</InputAdornment> },
              htmlInput: { maxLength: ROCKETCHAT_LIMITS.username + 1, autoComplete: 'off', spellCheck: false },
            }}
            fullWidth
          />
        </FormDialog>
      )}
    </Panel>
  );
}
